import React, { useState, useEffect, useCallback } from 'react';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { Sidebar, MobileHeader } from '../components/shared/Sidebar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import axios from '../lib/axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { 
  Swords, TrendingUp, TrendingDown, Clock, Users, Trophy,
  Calendar as CalendarIcon, Search, Filter, Trash2, Check,
  ChevronDown, Crown, Zap, Target, ArrowUpRight, ArrowDownRight,
  Flame, Shield, Star, DollarSign, Activity, X, AlertTriangle,
  Medal, RefreshCw
} from 'lucide-react';
import { formatCurrency, formatDateTime } from '../lib/utils';
import { usePageFilter } from '../contexts/PageFilterContext';
import { PageFilterTabs } from '../components/shared/PageFilterTabs';

// Animated background particles
function GamingParticles() {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      {[...Array(15)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 rounded-full"
          style={{
            background: ['#FF3366', '#FFD700', '#00F5FF', '#9945FF', '#00FF88'][i % 5],
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -20, 0],
            opacity: [0.2, 0.6, 0.2],
            scale: [1, 1.5, 1],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
          }}
        />
      ))}
    </div>
  );
}

// Animated counter component
function AnimatedNumber({ value, prefix = '', decimals = 0 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const end = parseFloat(value) || 0;
    const duration = 1500;
    const start = Date.now();
    const animate = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      setDisplay(end * (1 - Math.pow(1 - progress, 3)));
      if (progress < 1) requestAnimationFrame(animate);
    };
    animate();
  }, [value]);
  return <span className="font-mono tabular-nums">{prefix}{display.toFixed(decimals).toLocaleString()}</span>;
}

export default function BattleLogPage() {
  const { selectedPageId, dateRange } = usePageFilter();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [localDateRange, setLocalDateRange] = useState({ from: null, to: null });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setRefreshing(true);
      const params = { limit: 500 };
      if (selectedPageId) params.page_id = selectedPageId;
      if (dateRange.startDate) params.start_date = dateRange.startDate;
      if (dateRange.endDate) params.end_date = dateRange.endDate;
      const statsParams = {};
      if (selectedPageId) statsParams.page_id = selectedPageId;
      if (dateRange.startDate) statsParams.start_date = dateRange.startDate;
      if (dateRange.endDate) statsParams.end_date = dateRange.endDate;
      const [transRes, statsRes] = await Promise.all([
        axios.get('/transactions', { params }),
        axios.get('/transactions/stats', { params: statsParams })
      ]);
      setTransactions(transRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load battle data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedPageId, dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useAutoRefresh(fetchData, 15000);

  const handleApprove = async (transactionId) => {
    try {
      await axios.patch(`/transactions/${transactionId}/approve`);
      toast.success('Battle approved!');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const handleDelete = async (transactionId) => {
    try {
      await axios.delete(`/transactions/${transactionId}`);
      toast.success('Battle record deleted!');
      setShowDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  // Filter transactions
  const filteredTransactions = transactions.filter(t => {
    const matchesSearch = !searchQuery || 
      t.player_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.employee_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.game_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
    const matchesType = typeFilter === 'all' || t.transaction_type === typeFilter;
    
    let matchesDate = true;
    if (localDateRange.from || localDateRange.to) {
      const transDate = new Date(t.created_at);
      if (localDateRange.from && transDate < localDateRange.from) matchesDate = false;
      if (localDateRange.to && transDate > new Date(localDateRange.to.setHours(23, 59, 59))) matchesDate = false;
    }
    
    return matchesSearch && matchesStatus && matchesType && matchesDate;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#080810]">
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1 }} className="relative">
          <div className="w-20 h-20 rounded-full border-4 border-[#FF3366]/30 border-t-[#FF3366]" />
          <Swords className="absolute inset-0 m-auto w-8 h-8 text-[#FF3366]" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080810]" data-testid="battle-log-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <MobileHeader onMenuClick={() => setSidebarOpen(true)} />

      {/* Background Effects */}
      <GamingParticles />
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#FF3366]/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-[#FFD700]/5 rounded-full blur-[150px]" />
      </div>

      <main className="lg:ml-72 min-h-screen relative z-10 p-4 md:p-6 lg:p-8">
        {/* Epic Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6"
        >
          <div className="flex items-center gap-4">
            <motion.div
              animate={{ 
                boxShadow: ['0 0 20px #FF3366', '0 0 40px #FF3366', '0 0 20px #FF3366'],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ repeat: Infinity, duration: 3 }}
              className="p-3 rounded-xl bg-gradient-to-br from-[#FF3366]/20 to-[#FFD700]/20 border border-[#FF3366]/30"
            >
              <Swords className="w-8 h-8 text-[#FF3366]" />
            </motion.div>
            <div>
              <h1 className="text-3xl md:text-4xl font-black text-white" style={{ textShadow: '0 0 30px rgba(255,51,102,0.3)' }}>
                BATTLE LOG
              </h1>
              <p className="text-gray-400">Track all combat records</p>
            </div>
          </div>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={fetchData}
            disabled={refreshing}
            className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#FF3366] to-[#FFD700] text-black font-bold flex items-center gap-2"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            REFRESH
          </motion.button>
        </motion.div>

        {/* Page Filter */}
        <div className="mb-6">
          <PageFilterTabs />
        </div>

        {/* Volume Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            whileHover={{ scale: 1.02, y: -5 }}
          >
            <Card className="bg-[#0d0d15] border-0 p-4 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FFD700] to-transparent" />
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-[#FFD700]/10">
                  <Clock className="w-5 h-5 text-[#FFD700]" />
                </div>
                <span className="text-gray-400 text-sm">24H VOLUME</span>
              </div>
              <p className="text-2xl font-black text-[#FFD700]" data-testid="volume-24h">
                <AnimatedNumber value={stats?.volume_24h || 0} prefix="$" />
              </p>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            whileHover={{ scale: 1.02, y: -5 }}
          >
            <Card className="bg-[#0d0d15] border-0 p-4 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#00F5FF] to-transparent" />
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-[#00F5FF]/10">
                  <Activity className="w-5 h-5 text-[#00F5FF]" />
                </div>
                <span className="text-gray-400 text-sm">7 DAY VOLUME</span>
              </div>
              <p className="text-2xl font-black text-[#00F5FF]" data-testid="volume-7d">
                <AnimatedNumber value={stats?.volume_7d || 0} prefix="$" />
              </p>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            whileHover={{ scale: 1.02, y: -5 }}
          >
            <Card className="bg-[#0d0d15] border-0 p-4 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#00FF88] to-transparent" />
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-[#00FF88]/10">
                  <TrendingUp className="w-5 h-5 text-[#00FF88]" />
                </div>
                <span className="text-gray-400 text-sm">TOTAL DEPOSITS</span>
              </div>
              <p className="text-2xl font-black text-[#00FF88]" data-testid="total-deposits">
                <AnimatedNumber value={stats?.total_deposits || 0} prefix="$" />
              </p>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            whileHover={{ scale: 1.02, y: -5 }}
          >
            <Card className="bg-[#0d0d15] border-0 p-4 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FF3366] to-transparent" />
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-[#FF3366]/10">
                  <TrendingDown className="w-5 h-5 text-[#FF3366]" />
                </div>
                <span className="text-gray-400 text-sm">TOTAL WITHDRAWALS</span>
              </div>
              <p className="text-2xl font-black text-[#FF3366]" data-testid="total-withdrawals">
                <AnimatedNumber value={stats?.total_withdrawals || 0} prefix="$" />
              </p>
            </Card>
          </motion.div>
        </div>

        {/* Top 3 Depositors & Staff Leaderboard */}
        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Top 3 Depositors */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="bg-[#0d0d15] border-0 overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FFD700] to-transparent" />
              <div className="p-4 border-b border-white/5">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <Crown className="w-5 h-5 text-[#FFD700]" /> TOP WARRIORS
                </h3>
                <p className="text-xs text-gray-500">Highest depositors</p>
              </div>
              <div className="p-4 space-y-3">
                {stats?.top_depositors?.map((player, idx) => {
                  const medals = ['#FFD700', '#C0C0C0', '#CD7F32'];
                  const medalIcons = ['🥇', '🥈', '🥉'];
                  return (
                    <motion.div
                      key={player.player_id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 * idx }}
                      className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      <div className="relative">
                        <Avatar className="w-12 h-12 border-2" style={{ borderColor: medals[idx] }}>
                          <AvatarFallback 
                            className="text-white font-bold"
                            style={{ background: `linear-gradient(135deg, ${medals[idx]}40, ${medals[idx]}20)` }}
                          >
                            {player.player_name?.slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="absolute -top-1 -right-1 text-lg">{medalIcons[idx]}</span>
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-white">{player.player_name}</p>
                        <p className="text-xs text-gray-500">{player.transaction_count} battles</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-bold text-[#FFD700]">{formatCurrency(player.total_deposits)}</p>
                        <Badge className="bg-[#FFD700]/10 text-[#FFD700] border-0 text-xs">GOLD</Badge>
                      </div>
                    </motion.div>
                  );
                })}
                {(!stats?.top_depositors || stats.top_depositors.length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    <Trophy className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No warriors yet</p>
                  </div>
                )}
              </div>
            </Card>
          </motion.div>

          {/* Staff Leaderboard */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="bg-[#0d0d15] border-0 overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#9945FF] to-transparent" />
              <div className="p-4 border-b border-white/5">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#9945FF]" /> STAFF PERFORMANCE
                </h3>
                <p className="text-xs text-gray-500">Battle commanders ranking</p>
              </div>
              <ScrollArea className="h-[200px]">
                <div className="p-4 space-y-2">
                  {stats?.staff_leaderboard?.map((staff, idx) => (
                    <motion.div
                      key={staff.employee_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.05 * idx }}
                      className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-lg bg-[#9945FF]/20 flex items-center justify-center text-[#9945FF] font-bold">
                        #{idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-white truncate">{staff.employee_name}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span>{staff.total_transactions} battles</span>
                          <span>•</span>
                          <span className="text-[#00FF88]">{staff.approved_count} approved</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-bold text-white">{formatCurrency(staff.total_deposits)}</p>
                        <p className="text-xs text-[#FF3366]">-{formatCurrency(staff.total_withdrawals)}</p>
                      </div>
                    </motion.div>
                  ))}
                  {(!stats?.staff_leaderboard || stats.staff_leaderboard.length === 0) && (
                    <div className="text-center py-8 text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No staff data</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </Card>
          </motion.div>
        </div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="mb-6"
        >
          <Card className="bg-[#0d0d15] border-0 p-4">
            <div className="flex flex-wrap items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search player, staff, game..."
                  className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                  data-testid="search-transactions"
                />
              </div>

              {/* Status Filter */}
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                  data-testid="status-filter"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                </select>
              </div>

              {/* Type Filter */}
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                data-testid="type-filter"
              >
                <option value="all">All Types</option>
                <option value="deposit">Deposits</option>
                <option value="withdrawal">Withdrawals</option>
              </select>

              {/* Date Range */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="bg-white/5 border-white/10 text-white hover:bg-white/10"
                    data-testid="date-picker-btn"
                  >
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {localDateRange.from ? (
                      localDateRange.to ? (
                        <>
                          {format(localDateRange.from, 'MMM d')} - {format(localDateRange.to, 'MMM d')}
                        </>
                      ) : (
                        format(localDateRange.from, 'MMM d, yyyy')
                      )
                    ) : (
                      'Pick dates'
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#0d0d15] border-white/10" align="end">
                  <Calendar
                    mode="range"
                    selected={dateRange}
                    onSelect={setLocalDateRange}
                    numberOfMonths={2}
                    className="bg-[#0d0d15]"
                  />
                  <div className="p-3 border-t border-white/10 flex justify-end gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setLocalDateRange({ from: null, to: null })}
                      className="text-gray-400"
                    >
                      Clear
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>

              {/* Clear Filters */}
              {(searchQuery || statusFilter !== 'all' || typeFilter !== 'all' || localDateRange.from) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearchQuery('');
                    setStatusFilter('all');
                    setTypeFilter('all');
                    setLocalDateRange({ from: null, to: null });
                  }}
                  className="text-[#FF3366] hover:text-[#FF3366] hover:bg-[#FF3366]/10"
                >
                  <X className="w-4 h-4 mr-1" /> Clear
                </Button>
              )}
            </div>

            {/* Stats Summary */}
            <div className="flex items-center gap-6 mt-4 pt-4 border-t border-white/5 text-sm">
              <span className="text-gray-400">
                Showing <span className="text-white font-bold">{filteredTransactions.length}</span> of {transactions.length} battles
              </span>
              <span className="text-gray-400">
                Pending: <span className="text-[#FFD700] font-bold">{stats?.pending_count || 0}</span>
              </span>
              <span className="text-gray-400">
                Approved: <span className="text-[#00FF88] font-bold">{stats?.approved_count || 0}</span>
              </span>
            </div>
          </Card>
        </motion.div>

        {/* Transactions Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="bg-[#0d0d15] border-0 overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FF3366] to-transparent" />
            
            {/* Table Header */}
            <div className="hidden lg:grid grid-cols-12 gap-4 p-4 border-b border-white/5 text-xs font-bold text-gray-500 uppercase">
              <div className="col-span-2">Warrior</div>
              <div className="col-span-2">Commander</div>
              <div className="col-span-2">Arena</div>
              <div className="col-span-1">Type</div>
              <div className="col-span-2 text-right">Amount</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>

            <ScrollArea className="h-[500px]">
              <div className="divide-y divide-white/5">
                {filteredTransactions.map((trans, idx) => {
                  const isDeposit = trans.transaction_type === 'deposit';
                  return (
                    <motion.div
                      key={trans.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.02 }}
                      className="p-4 hover:bg-white/5 transition-colors"
                      data-testid={`transaction-${trans.id}`}
                    >
                      {/* Mobile View */}
                      <div className="lg:hidden space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${isDeposit ? 'bg-[#00FF88]/10' : 'bg-[#FF3366]/10'}`}>
                              {isDeposit ? <ArrowUpRight className="w-4 h-4 text-[#00FF88]" /> : <ArrowDownRight className="w-4 h-4 text-[#FF3366]" />}
                            </div>
                            <div>
                              <p className="font-bold text-white">{trans.player_name}</p>
                              <p className="text-xs text-gray-500">{trans.employee_name} • {trans.game_name}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`font-mono font-bold ${isDeposit ? 'text-[#00FF88]' : 'text-[#FF3366]'}`}>
                              {isDeposit ? '+' : '-'}{formatCurrency(trans.deposit_amount)}
                            </p>
                            <Badge className={`${
                              trans.status === 'approved' 
                                ? 'bg-[#00FF88]/10 text-[#00FF88]' 
                                : 'bg-[#FFD700]/10 text-[#FFD700]'
                            } border-0 text-xs`}>
                              {trans.status.toUpperCase()}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex items-center justify-between pt-2 border-t border-white/5">
                          <span className="text-xs text-gray-500">{formatDateTime(trans.created_at)}</span>
                          <div className="flex items-center gap-2">
                            {trans.status === 'pending' && (
                              <Button
                                size="sm"
                                onClick={() => handleApprove(trans.id)}
                                className="bg-[#00FF88]/10 text-[#00FF88] hover:bg-[#00FF88]/20 h-8 px-3"
                              >
                                <Check className="w-3 h-3 mr-1" /> Approve
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setShowDeleteConfirm(trans.id)}
                              className="text-[#FF3366] hover:text-[#FF3366] hover:bg-[#FF3366]/10 h-8 px-3"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Desktop View */}
                      <div className="hidden lg:grid grid-cols-12 gap-4 items-center">
                        <div className="col-span-2 flex items-center gap-3">
                          <Avatar className="w-10 h-10 border" style={{ borderColor: isDeposit ? '#00FF88' : '#FF3366' }}>
                            <AvatarFallback className="text-white text-xs" style={{ background: isDeposit ? '#00FF8820' : '#FF336620' }}>
                              {trans.player_name?.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="font-bold text-white truncate">{trans.player_name}</p>
                            <p className="text-xs text-gray-500">{formatDateTime(trans.created_at)}</p>
                          </div>
                        </div>
                        
                        <div className="col-span-2">
                          <p className="text-white">{trans.employee_name}</p>
                          <p className="text-xs text-gray-500">Commander</p>
                        </div>
                        
                        <div className="col-span-2">
                          <Badge className="bg-[#9945FF]/10 text-[#9945FF] border-0">{trans.game_name}</Badge>
                        </div>
                        
                        <div className="col-span-1">
                          <Badge className={`${isDeposit ? 'bg-[#00FF88]/10 text-[#00FF88]' : 'bg-[#FF3366]/10 text-[#FF3366]'} border-0`}>
                            {isDeposit ? 'IN' : 'OUT'}
                          </Badge>
                        </div>
                        
                        <div className="col-span-2 text-right">
                          <p className={`font-mono font-bold ${isDeposit ? 'text-[#00FF88]' : 'text-[#FF3366]'}`}>
                            {isDeposit ? '+' : '-'}{formatCurrency(trans.deposit_amount)}
                          </p>
                          {trans.loaded_amount > 0 && trans.loaded_amount !== trans.deposit_amount && (
                            <p className="text-xs text-gray-500">Loaded: {formatCurrency(trans.loaded_amount)}</p>
                          )}
                        </div>
                        
                        <div className="col-span-1">
                          <Badge className={`${
                            trans.status === 'approved' 
                              ? 'bg-[#00FF88]/10 text-[#00FF88]' 
                              : 'bg-[#FFD700]/10 text-[#FFD700]'
                          } border-0`}>
                            {trans.status === 'approved' ? <Check className="w-3 h-3 mr-1" /> : <Clock className="w-3 h-3 mr-1" />}
                            {trans.status.toUpperCase()}
                          </Badge>
                        </div>
                        
                        <div className="col-span-2 flex items-center justify-end gap-2">
                          {trans.status === 'pending' && (
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleApprove(trans.id)}
                              className="px-3 py-1.5 rounded-lg bg-[#00FF88]/10 text-[#00FF88] hover:bg-[#00FF88]/20 text-sm font-bold flex items-center gap-1"
                              data-testid={`approve-${trans.id}`}
                            >
                              <Check className="w-3 h-3" /> Approve
                            </motion.button>
                          )}
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setShowDeleteConfirm(trans.id)}
                            className="p-2 rounded-lg bg-[#FF3366]/10 text-[#FF3366] hover:bg-[#FF3366]/20"
                            data-testid={`delete-${trans.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </motion.button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}

                {filteredTransactions.length === 0 && (
                  <div className="p-12 text-center text-gray-500">
                    <Swords className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-bold mb-1">No battles found</p>
                    <p className="text-sm">Try adjusting your filters</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </Card>
        </motion.div>
      </main>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm"
            >
              <Card className="bg-[#0d0d15] border-[#FF3366]/30 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 rounded-xl bg-[#FF3366]/20">
                    <AlertTriangle className="w-6 h-6 text-[#FF3366]" />
                  </div>
                  <h2 className="text-xl font-bold text-white">DELETE BATTLE?</h2>
                </div>
                <p className="text-gray-400 mb-6">
                  This action cannot be undone. The battle record will be permanently removed.
                </p>
                <div className="flex gap-3">
                  <Button
                    onClick={() => handleDelete(showDeleteConfirm)}
                    className="flex-1 bg-gradient-to-r from-[#FF3366] to-[#FF6B00] text-white font-bold"
                    data-testid="confirm-delete-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> DELETE
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteConfirm(null)}
                    className="flex-1 border-white/10"
                  >
                    Cancel
                  </Button>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
